import styled from "styled-components";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import AutoFixOffIcon from "@mui/icons-material/AutoFixOff";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import DoneIcon from "@mui/icons-material/Done";
import ClearIcon from "@mui/icons-material/Clear";

import CropperView from "components/photo/CropperView";
import { useImageCrop } from "hooks/useImageCrop";
import { CSS_VARIABLES } from "enums/cssVariables";

type Props = {
    previewUrl: string;
    backgroundRemovedUrl: string | null;
    onRemoveBackground: (area: any) => void;
    onResetRemoveBackground: () => void;
    onClear: () => void;
    onValidate: (area: any) => void;
};

const PhotoEditor = ({
    previewUrl,
    backgroundRemovedUrl,
    onRemoveBackground,
    onResetRemoveBackground,
    onClear,
    onValidate,
}: Props) => {
    const normalCrop = useImageCrop();
    const bgCrop = useImageCrop();

    const isBgRemoved = Boolean(backgroundRemovedUrl);

    const activeCrop = isBgRemoved ? bgCrop : normalCrop;
    const image = isBgRemoved ? backgroundRemovedUrl! : previewUrl;

    const handleClear = () => {
        normalCrop.reset();
        bgCrop.reset();
        onClear();
    };

    return (
        <>
            <ImageWrapper>
                <CropperView
                    image={image}
                    crop={activeCrop.crop}
                    zoom={activeCrop.zoom}
                    onCropChange={activeCrop.setCrop}
                    onZoomChange={activeCrop.setZoom}
                    onCropComplete={activeCrop.onCropComplete}
                />
            </ImageWrapper>

            <IconButtonWrapper>
                <IconOutlinedButton
                    onClick={() =>
                        activeCrop.setZoom((z) => Math.min(z + 0.2, 3))
                    }
                >
                    <ZoomInIcon />
                </IconOutlinedButton>

                <IconOutlinedButton
                    onClick={() =>
                        activeCrop.setZoom((z) => Math.max(z - 0.2, 1))
                    }
                >
                    <ZoomOutIcon />
                </IconOutlinedButton>

                <IconOutlinedButton
                    onClick={() =>
                        isBgRemoved
                            ? onResetRemoveBackground()
                            : onRemoveBackground(normalCrop.croppedArea)
                    }
                >
                    {isBgRemoved ? <AutoFixOffIcon /> : <AutoFixHighIcon />}
                </IconOutlinedButton>

                <Spacer />

                <IconButton onClick={handleClear}>
                    <ClearIcon />
                </IconButton>
                <IconButton onClick={() => onValidate(activeCrop.croppedArea)}>
                    <DoneIcon />
                </IconButton>
            </IconButtonWrapper>
        </>
    );
};

const ImageWrapper = styled.div`
    position: relative;
    width: 100%;
    height: 400px;
    margin-top: 20px;
    background-color: #fafafa;
    border-radius: 12px;
    overflow: hidden;
`;

const IconButtonWrapper = styled.div`
    display: flex;
    margin-top: 5px;
    width: 100%;
    justify-content: space-between;
    align-items: center;
    gap: 5px;
`;

const Spacer = styled.div`
    flex: 1;
`;

const IconOutlinedButton = styled.button.attrs({ type: "button" })`
    padding: 6px 12px;
    border-radius: 8px;
    cursor: pointer;
    border: 1px solid var(${CSS_VARIABLES.PRIMARY_BUTTON_BACKGROUND_COLOR});
    background-color: #fafafa;
    color: var(${CSS_VARIABLES.PRIMARY_BUTTON_BACKGROUND_COLOR});
`;

const IconButton = styled(IconOutlinedButton)`
    background-color: var(${CSS_VARIABLES.PRIMARY_BUTTON_BACKGROUND_COLOR});
    border: 1px solid var(${CSS_VARIABLES.PRIMARY_BUTTON_BACKGROUND_COLOR});
    color: #fff;
`;

export default PhotoEditor;
